// =============================================================================
// Shared globe scenery helpers (Three.js).
//
// Extracted from EarthLandingPage so the super-admin "Kokpit globu"
// (EarthDashboard) can render the SAME beautiful Earth as the public landing
// globe: GeoJSON country outlines + a soft atmospheric edge-glow.
//
// Regression this fixes: /earth-texture.jpg ships empty (0 bytes), so the
// texture load always fails. EarthLandingPage degraded gracefully (clean blue
// ocean + country borders + atmosphere); the cockpit globe had neither and
// fell back to a flat procedural-gradient "blue ball". Both globes now share
// one implementation.
// =============================================================================
import * as THREE from 'three';

// Cache image loads (textures) by URL so a scene rebuild (markers change,
// route change landing↔cockpit) doesn't re-download the ~1 MB of earth maps.
THREE.Cache.enabled = true;

// lat/lon (degrees) → point on a sphere of the given radius.
const latLonTo3D = (lat: number, lon: number, r: number): THREE.Vector3 => {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -(r * Math.sin(phi) * Math.cos(theta)),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta)
  );
};

// Subtle lat/lon grid shown immediately (and while GeoJSON loads); returned so
// the border loader can remove it once real borders arrive, and kept as a
// graceful fallback if the GeoJSON fetch fails.
const createSimplifiedBorders = (globe: THREE.Mesh, radius: number): THREE.Group => {
  const borderGroup = new THREE.Group();
  borderGroup.name = 'simplifiedBorders';

  const gridMaterial = new THREE.LineBasicMaterial({
    color: 0x2244aa,
    transparent: true,
    opacity: 0.12,
  });

  // Latitude lines every 30 degrees
  for (let lat = -60; lat <= 60; lat += 30) {
    const points: THREE.Vector3[] = [];
    for (let lon = -180; lon <= 180; lon += 5) {
      points.push(latLonTo3D(lat, lon, radius));
    }
    borderGroup.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(points), gridMaterial));
  }

  // Longitude lines every 30 degrees
  for (let lon = -180; lon < 180; lon += 30) {
    const points: THREE.Vector3[] = [];
    for (let lat = -90; lat <= 90; lat += 5) {
      points.push(latLonTo3D(lat, lon, radius));
    }
    borderGroup.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(points), gridMaterial));
  }

  globe.add(borderGroup);
  return borderGroup;
};

// Parsed Natural Earth GeoJSON, fetched once per page load and shared by every
// globe/scene rebuild (marker changes remount the whole Three scene).
type CountryGeoJson = { features?: Array<{ geometry: { type: string; coordinates: unknown } }> };
let countryGeoJsonPromise: Promise<CountryGeoJson> | null = null;

function fetchCountryGeoJson(): Promise<CountryGeoJson> {
  if (!countryGeoJsonPromise) {
    countryGeoJsonPromise = fetch(
      'https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_110m_admin_0_countries.geojson'
    ).then((res) => {
      if (!res.ok) throw new Error('Failed to fetch');
      return res.json();
    });
    // A failed fetch must not poison the cache for the rest of the session.
    countryGeoJsonPromise.catch(() => {
      countryGeoJsonPromise = null;
    });
  }
  return countryGeoJsonPromise;
}

/**
 * Add Natural Earth country outlines to a globe mesh (they ride on the globe so
 * they rotate with it). A faint lat/lon grid renders immediately and is removed
 * once the GeoJSON borders load; on fetch failure the grid stays as a fallback.
 */
export function addCountryBorders(globe: THREE.Mesh, radius: number): THREE.Group {
  const borderGroup = new THREE.Group();
  borderGroup.name = 'geojsonBorders';

  const borderMaterial = new THREE.LineBasicMaterial({
    color: 0x4488ff,
    transparent: true,
    // Soft: geography now comes from the photo texture — the outlines are a
    // subtle "space control" accent, not the primary detail.
    opacity: 0.35,
    linewidth: 1,
  });

  // Grid lines are visible while GeoJSON loads.
  const gridGroup = createSimplifiedBorders(globe, radius);

  fetchCountryGeoJson()
    .then((geojson: CountryGeoJson) => {
      (geojson.features ?? []).forEach((feature) => {
        const geometry = feature.geometry;

        const processCoordinates = (coords: number[][]) => {
          const points: THREE.Vector3[] = [];
          coords.forEach((coord) => {
            points.push(latLonTo3D(coord[1], coord[0], radius));
          });
          if (points.length > 1) {
            borderGroup.add(
              new THREE.Line(new THREE.BufferGeometry().setFromPoints(points), borderMaterial)
            );
          }
        };

        if (geometry.type === 'Polygon') {
          (geometry.coordinates as number[][][]).forEach((ring) => processCoordinates(ring));
        } else if (geometry.type === 'MultiPolygon') {
          (geometry.coordinates as number[][][][]).forEach((polygon) =>
            polygon.forEach((ring) => processCoordinates(ring))
          );
        }
      });

      // Remove grid lines once real borders are in (prevent overlap).
      if (gridGroup.parent) {
        gridGroup.parent.remove(gridGroup);
      }
      globe.add(borderGroup);
    })
    .catch((err) => {
      console.warn('Failed to load country borders, keeping grid lines:', err);
      // Grid lines remain as fallback.
    });

  return borderGroup;
}

// -----------------------------------------------------------------------------
// Earth textures (NASA Blue Marble derivatives shipped in public/):
//   /earth-texture.jpg — daylight surface (2048×1024)
//   /earth-night.png   — city night lights
//   /earth-clouds.png  — cloud cover (white on black → alpha map)
// -----------------------------------------------------------------------------
const textureLoader = new THREE.TextureLoader();

/**
 * Load the daylight Earth photo onto a globe material. On success the material
 * color is reset to white so the photo shows true colors (both globes tint the
 * bare sphere ocean-blue as the no-texture fallback, which would darken a map).
 */
export function applyEarthSurface(
  material: THREE.MeshStandardMaterial | THREE.MeshPhongMaterial,
  onLoaded?: (texture: THREE.Texture) => void
): void {
  textureLoader.load(
    '/earth-texture.jpg',
    (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      material.map = texture;
      material.color.set(0xffffff);
      material.needsUpdate = true;
      onLoaded?.(texture);
    },
    undefined,
    () => {
      // No texture: the ocean-blue sphere + GeoJSON borders remain — the same
      // graceful fallback both globes shipped with. No fake continents.
    }
  );
}

/**
 * Load the real cloud-cover map onto a cloud-shell material as an alpha map
 * (white clouds → opaque, black ocean gaps → transparent). Until it loads the
 * shell keeps its plain translucent-veil look.
 */
export function applyCloudLayer(
  material: THREE.MeshStandardMaterial | THREE.MeshPhongMaterial
): void {
  textureLoader.load(
    '/earth-clouds.png',
    (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      // Additive: black gaps vanish, clouds glow softly white. This also hides
      // the hard edges of the (low-res) cover map that an alpha map exposes.
      material.map = texture;
      material.blending = THREE.AdditiveBlending;
      // Front side only — DoubleSide would show the far hemisphere's clouds
      // through the gaps and read as noise.
      material.side = THREE.FrontSide;
      material.opacity = 0.4;
      material.needsUpdate = true;
    },
    undefined,
    () => {
      // Keep the plain translucent veil.
    }
  );
}

/** Load the night-lights map (city lights) for use as an emissive map. */
export function loadNightLights(onLoaded: (texture: THREE.Texture) => void): void {
  textureLoader.load(
    '/earth-night.png',
    (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      onLoaded(texture);
    },
    undefined,
    () => {
      // Night mode falls back to the untextured emissive glow.
    }
  );
}

/**
 * Soft, edge-only atmospheric glow (a BackSide shader shell). Add the returned
 * mesh to the scene at a radius slightly larger than the globe.
 */
export function createAtmosphere(radius: number): THREE.Mesh {
  return new THREE.Mesh(
    new THREE.SphereGeometry(radius, 64, 64),
    new THREE.ShaderMaterial({
      transparent: true,
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        c: { value: 0.15 }, // controls glow spread
        p: { value: 8.0 }, // edge falloff sharpness
        glowColor: { value: new THREE.Color(0x2244aa) },
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vPositionNormal;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          vPositionNormal = normalize((modelViewMatrix * vec4(position, 1.0)).xyz);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform float c;
        uniform float p;
        uniform vec3 glowColor;
        varying vec3 vNormal;
        varying vec3 vPositionNormal;
        void main() {
          float intensity = pow(c - dot(vNormal, vPositionNormal), p);
          intensity = clamp(intensity, 0.0, 1.0);
          gl_FragColor = vec4(glowColor, intensity * 0.25);
        }
      `,
    })
  );
}
